/**
 * Home layout "Simple" (2026-10-24, round 3 10-25): what Opus Clip and Vizard do, for CherryClipz.
 * One headline, the product as three numbered steps, one Upload bar, My Library you can't miss.
 * Then the REAL editor on a sample film (or your own video), the three ways to cut with a "Works
 * with" row, one section each for the phone app, Claude / ChatGPT and Premiere Pro, the demo
 * films, and pricing.
 *
 * Upload, sign-in, paste-a-link and the library are the real ones (SimpleUpload, OnboardingFlow,
 * /my-library). Three skins (cream / red / dark) share the markup; the owner picks the live one
 * from the site-layout switcher. CSS is scoped under `.ccs`.
 */
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { userContext } from "../../../../stores/users/userContext";
import { messageContext } from "../../../../stores/messages/messageContext";
import apiRequest from "../../../../utils/apiClient";
import { hostApi, pluginDownloadLinkUrl } from "../../../constants";
import { ROUTES } from "../../../../constants";
import logoDark from "../../../../assets/brand/logo_site.svg";
import logoLight from "../../../../assets/brand/logo_site_onlight.svg";
import { useHomeFonts, storeForThisPhone } from "../components/home3/kit";
import { QR_PATH, QR_SIZE } from "../components/home3/filmData";
import { APP_STORE_URL, PLAY_STORE_URL, storeLink } from "../constants/homeLayoutsText";
import { CONNECTOR_VIDEO } from "../../../connector/connectorGuideConstants";
import { ICON } from "../components/home3/brandIcons";
import { DEMO_YOUTUBE_ID } from "../components/FlagshipPage";
import FlagshipLiveEt from "../components/FlagshipLiveEt";
import {
  SIMPLE_AI,
  SIMPLE_APP,
  SIMPLE_DEMO,
  SIMPLE_DEMOS,
  SIMPLE_FOOTER,
  SIMPLE_HEADLINES,
  SIMPLE_HERO,
  SIMPLE_NAV,
  SIMPLE_PARTNERS,
  SIMPLE_PREMIERE,
  SIMPLE_PRICING,
  SIMPLE_WAYS,
} from "../constants/simpleHomeText";
import { SIMPLE_CSS } from "./simpleCss";
import SimpleUpload from "./SimpleUpload";
import { useSignInSheet } from "./UploadBlock";
import logoMark from "../../../../assets/brand/logo_site_onlight.svg";
import type { HomeHeadline, HomeSkin } from "../siteLayout/siteSettings";

const scrollToId = (id: string) => {
  try {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  } catch (e) {
    /* ignore */
  }
};

/**
 * The real editor (the same live ET the Flagship page shows, with its sample / your-videos
 * picker), mounted only when the visitor scrolls near it: it is a whole app in an iframe, and the
 * Upload bar above must not wait for it.
 */
const LazyEditor: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setNear(true);
      return;
    }
    const io = new IntersectionObserver((es) => es.some((e) => e.isIntersecting) && setNear(true), { rootMargin: "400px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className="editor">
      {near ? <FlagshipLiveEt title="Film Reel — live" defaultMode="desktop" heightPx={600} defaultZoom={0.82} /> : <div className="editor-wait" />}
    </div>
  );
};

/** One partner mark, in its own colours where it has them. */
const PartnerMark: React.FC<{ k: (typeof SIMPLE_PARTNERS)[number]["key"]; size?: number }> = ({ k, size = 28 }) => {
  if (k === "pr")
    return (
      <i className="pr-mark" style={{ width: size, height: size, fontSize: size * 0.42 }}>
        Pr
      </i>
    );
  // Multi-colour marks are the brands' own files, unchanged (public/media/brand/), never one
  // flat colour (Isaac: no green-only Play logo).
  if (k === "gdrive" || k === "gplay" || k === "gemini") {
    const file = { gdrive: "google-drive", gplay: "google-play", gemini: "gemini" }[k];
    return <img src={`/media/brand/${file}.svg`} alt="" width={size} height={size} style={{ objectFit: "contain", display: "block" }} />;
  }
  const fill = k === "claude" ? "#D97757" : k === "dropbox" ? "#0061FF" : "currentColor";
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
      <path fill={fill} d={ICON[k]} />
    </svg>
  );
};

/** A muted app recording that plays only while it is on screen. */
const LoopClip: React.FC<{ src: string; poster: string; label: string }> = ({ src, poster, label }) => {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = ref.current;
    if (!v || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (es) =>
        es.forEach((e) => {
          if (e.isIntersecting) {
            const p = v.play();
            if (p && p.catch) p.catch((): void => undefined);
          } else v.pause();
        }),
      { threshold: 0.3 },
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);
  return <video ref={ref} src={src} poster={poster} muted loop playsInline preload="none" aria-label={label} />;
};

/** The ChatGPT film: a poster with a big "Play with sound" until clicked, then the real player. */
const SoundFilm: React.FC = () => {
  const ref = useRef<HTMLVideoElement>(null);
  const [on, setOn] = useState(false);
  const play = () => {
    setOn(true);
    const v = ref.current;
    if (!v) return;
    v.muted = false;
    v.controls = true;
    const p = v.play();
    if (p && p.catch) p.catch((): void => undefined);
  };
  return (
    <div className="film" style={{ aspectRatio: "9 / 16", maxWidth: 360, margin: "0 auto" }}>
      <video ref={ref} src={CONNECTOR_VIDEO.src} poster={CONNECTOR_VIDEO.poster} playsInline preload="none" aria-label={CONNECTOR_VIDEO.caption} />
      {!on && (
        <button type="button" className="play" onClick={play} aria-label={SIMPLE_AI.sound}>
          <span>{SIMPLE_AI.sound}</span>
        </button>
      )}
    </div>
  );
};

type DemoId = "demo" | "tour" | "spot";
const DEMO_MEDIA: Record<DemoId, { poster: string; src?: string; youtube?: string; tall?: boolean }> = {
  demo: { poster: `https://i.ytimg.com/vi/${DEMO_YOUTUBE_ID}/hqdefault.jpg`, youtube: DEMO_YOUTUBE_ID },
  tour: { poster: "/home/hub-tour.jpg", src: "/home/hub-tour.mp4", tall: true },
  spot: { poster: "/home/stop-cutting.jpg", src: "/home/stop-cutting.mp4", tall: true },
};

const DemoModal: React.FC<{ id: DemoId; onClose: () => void }> = ({ id, onClose }) => {
  const m = DEMO_MEDIA[id];
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`box${m.tall ? " tall" : ""}`}>
        {m.youtube ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${m.youtube}?autoplay=1&rel=0`}
            title={SIMPLE_DEMOS.items[0].h}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
          />
        ) : (
          <video src={m.src} poster={m.poster} controls autoPlay playsInline />
        )}
        <button type="button" className="x" onClick={onClose}>
          ✕ {SIMPLE_DEMOS.close}
        </button>
      </div>
    </div>
  );
};

const Qr: React.FC = () => (
  <svg viewBox={`-2 -2 ${QR_SIZE + 4} ${QR_SIZE + 4}`} role="img" aria-label={SIMPLE_APP.qr}>
    <rect x="-2" y="-2" width={QR_SIZE + 4} height={QR_SIZE + 4} fill="#fff" />
    <path d={QR_PATH} stroke="#17110f" fill="none" />
  </svg>
);

const GoogleG: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

interface SimplePageProps {
  skin?: HomeSkin;
  headline?: HomeHeadline;
}

const SimplePage: React.FC<SimplePageProps> = ({ skin = "cream", headline = "becomes" }) => {
  const h = SIMPLE_HEADLINES[headline] || SIMPLE_HEADLINES.becomes;
  useHomeFonts();
  const navigate = useNavigate();
  const userStore = useContext(userContext);
  const messageStore = useContext(messageContext);
  const isAuthed = userStore.isUserAuth();
  const email: string = isAuthed && userStore.user ? userStore.getUserEmail() || "" : "";

  // One sign-in sheet for the whole page (UploadBlock's): `then` runs once they are in, e.g.
  // the upload they already chose, the link they pasted, or opening the library.
  const { requireSignIn, sheet } = useSignInSheet();
  // "Download the panel" starts the download right here (signing in first if needed): the
  // backend hands a signed, short-lived link to any signed-in account (free since 2026-10-25).
  const [panelBusy, setPanelBusy] = useState(false);
  const fetchPanel = async () => {
    setPanelBusy(true);
    try {
      const res = await apiRequest(pluginDownloadLinkUrl, { method: "GET" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.path) throw new Error(data.error || SIMPLE_PREMIERE.downloadFailed);
      window.location.href = `${hostApi}${data.path}`;
    } catch (e) {
      messageStore.setMessage((e as Error)?.message || SIMPLE_PREMIERE.downloadFailed, "error");
    } finally {
      setPanelBusy(false);
    }
  };
  const downloadPanel = () => {
    if (userStore.isUserAuth()) fetchPanel();
    else requireSignIn(SIMPLE_PREMIERE.signInFirst, fetchPanel);
  };

  const goLibrary = () => {
    if (userStore.isUserAuth()) navigate(ROUTES.MY_LIBRARY);
    else requireSignIn("", () => navigate(ROUTES.MY_LIBRARY));
  };
  const openSignIn = () => requireSignIn("", () => undefined);

  const [demo, setDemo] = useState<DemoId | null>(null);
  const phoneStore = useMemo(() => storeForThisPhone(), []);
  const dark = skin === "dark" || skin === "red";
  const logo = dark ? logoDark : logoLight;

  return (
    <div className="ccs" data-skin={skin} id="ccs-top">
      <style>{SIMPLE_CSS}</style>

      <nav className="top">
        <div className="nav-in">
          <button type="button" className="brand" onClick={() => scrollToId("ccs-top")} aria-label="CherryClipz">
            <img src={skin === "red" ? logoDark : logo} alt="" />
            <span>
              Cherry<b>Clipz</b>
            </span>
          </button>
          <div className="links">
            <button type="button" onClick={() => scrollToId("how")}>{SIMPLE_NAV.how}</button>
            <button type="button" onClick={() => scrollToId("app")}>{SIMPLE_NAV.app}</button>
            <button type="button" onClick={() => scrollToId("ai")}>{SIMPLE_NAV.ai}</button>
            <button type="button" onClick={() => scrollToId("premiere")}>{SIMPLE_NAV.premiere}</button>
            <button type="button" onClick={() => navigate(ROUTES.PRICING)}>{SIMPLE_NAV.pricing}</button>
          </div>
          <div className="right">
            {phoneStore && (
              <a className="getapp" href={storeLink(phoneStore, "simple-nav")}>
                {SIMPLE_NAV.getApp}
              </a>
            )}
            <button type="button" className="lib" onClick={goLibrary}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              </svg>
              <span className="lib-word">{SIMPLE_NAV.library}</span>
            </button>
            {isAuthed ? (
              <button type="button" className="signin" onClick={goLibrary} title={email}>
                <span className="who signin-word">{email || SIMPLE_NAV.library}</span>
              </button>
            ) : (
              <button type="button" className="signin" onClick={openSignIn}>
                <GoogleG />
                <span className="signin-word">{SIMPLE_NAV.signIn}</span>
              </button>
            )}
          </div>
        </div>
      </nav>

      <header className="hero">
        <div className="hero-in">
          <h1>
            {h.l1}
            <br />
            {h.l2}
            <em>{h.hot}</em>
          </h1>
          <ol className="play-steps">
            {SIMPLE_HERO.steps.map((t, k) => (
              <li key={t}>
                <b>{k + 1}</b>
                {t}
              </li>
            ))}
          </ol>
          <SimpleUpload requireSignIn={requireSignIn} />
          <div className="ticks">
            {SIMPLE_HERO.ticks.map((t) => (
              <span key={t}>{t}</span>
            ))}
          </div>
          <div className="back">
            {SIMPLE_HERO.back}{" "}
            <button type="button" onClick={goLibrary}>
              {SIMPLE_HERO.backLink}
            </button>
          </div>
        </div>
      </header>

      <section className="shot" id="how">
        <div className="shot-head">
          <span className="tag">{SIMPLE_DEMO.tag}</span>
          <h2>{SIMPLE_DEMO.title}</h2>
          <p className="lede">{SIMPLE_DEMO.lede}</p>
        </div>
        <LazyEditor />
      </section>

      <section className="s" id="ways">
        <div className="wrap center">
          <h2>{SIMPLE_WAYS.title}</h2>
          <div className="works">
            <span className="works-label">
              <img src={logoMark} alt="" />
              {SIMPLE_WAYS.worksWith}
            </span>
            {SIMPLE_PARTNERS.map((p) => (
              <span className="partner" key={p.key}>
                <PartnerMark k={p.key} />
                {p.name}
              </span>
            ))}
          </div>
          <div className="ways">
            {SIMPLE_WAYS.items.map((w, k) => (
              <div className="way" key={w.id}>
                <div className="way-icons">
                  {w.id === "self" ? (
                    <>
                      <img src={logoMark} alt="" className="way-cc" />
                      <PartnerMark k="appstore" size={26} />
                      <PartnerMark k="gplay" size={26} />
                    </>
                  ) : w.id === "ai" ? (
                    <>
                      <PartnerMark k="claude" size={30} />
                      <PartnerMark k="openai" size={30} />
                    </>
                  ) : (
                    <PartnerMark k="pr" size={32} />
                  )}
                </div>
                <span className="way-n">{k + 1}</span>
                <h3>{w.h}</h3>
                <p>{w.p}</p>
                <button
                  type="button"
                  className="textlink"
                  onClick={() =>
                    w.id === "self" ? scrollToId("ccs-top") : w.id === "ai" ? scrollToId("ai") : navigate(ROUTES.PLUGIN_DOWNLOAD)
                  }
                >
                  {w.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="s" id="app">
        <div className="wrap">
          <div className="panel split">
            <div>
              <span className="eyebrow">{SIMPLE_APP.eyebrow}</span>
              <h2>{SIMPLE_APP.title}</h2>
              <p className="lede">{SIMPLE_APP.body}</p>
              <p className="lede" style={{ marginTop: 10, fontWeight: 600 }}>{SIMPLE_APP.free}</p>
              <div className="badges">
                <a href={storeLink(APP_STORE_URL, "simple-app")} target="_blank" rel="noopener noreferrer">
                  <img className="as" src={dark ? "/app/appstore-badge-white.svg" : "/app/appstore-badge-black.svg"} alt="Download on the App Store" />
                </a>
                <a href={storeLink(PLAY_STORE_URL, "simple-app")} target="_blank" rel="noopener noreferrer">
                  <img className="gp" src="/app/googleplay-badge.png" alt="Get it on Google Play" />
                </a>
              </div>
              <div className="qr">
                <Qr />
                <span>{SIMPLE_APP.qr}</span>
              </div>
              <div className="btns">
                <button type="button" className="textlink" onClick={() => navigate(ROUTES.APP)}>
                  {SIMPLE_APP.open}
                </button>
              </div>
            </div>
            <div className="media phones">
              <div className="phone">
                <LoopClip src="/home/app-transcript.mp4" poster="/home/app-transcript.jpg" label="The CherryClipz phone app: every sentence is a clip" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="s" id="ai">
        <div className="wrap">
          <div className="split flip">
            <div className="media">
              <SoundFilm />
            </div>
            <div>
              <span className="eyebrow">{SIMPLE_AI.eyebrow}</span>
              <div className="linked" aria-label="Claude and ChatGPT, connected to CherryClipz">
                <span className="tile">
                  <PartnerMark k="claude" size={34} />
                  Claude
                </span>
                <span className="wire" aria-hidden />
                <span className="tile cc">
                  <img src={logoMark} alt="" />
                  CherryClipz
                </span>
                <span className="wire" aria-hidden />
                <span className="tile">
                  <PartnerMark k="openai" size={34} />
                  ChatGPT
                </span>
              </div>
              <h2>{SIMPLE_AI.title}</h2>
              <p className="lede">{SIMPLE_AI.body}</p>
              <ol className="asksteps">
                {SIMPLE_AI.steps.map((s, k) => (
                  <li key={s}>
                    <b>{k + 1}</b>
                    {s}
                  </li>
                ))}
              </ol>
              <div className="say">{SIMPLE_AI.example}</div>
              <div className="btns">
                <button type="button" className="btn solid" onClick={() => navigate(ROUTES.AI_CONNECTOR)}>
                  {SIMPLE_AI.cta}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="s" id="premiere">
        <div className="wrap">
          <div className="panel pr-panel">
            <div className="pr-words">
              <div>
                <div className="pr-tag">
                  <PartnerMark k="pr" size={36} />
                  <span className="eyebrow" style={{ margin: 0 }}>{SIMPLE_PREMIERE.eyebrow}</span>
                </div>
                <h2>{SIMPLE_PREMIERE.title}</h2>
                <p className="lede">{SIMPLE_PREMIERE.body}</p>
              </div>
              <div className="btns">
                <button type="button" className="btn solid" onClick={downloadPanel} disabled={panelBusy}>
                  {panelBusy ? SIMPLE_PREMIERE.downloading : SIMPLE_PREMIERE.download}
                </button>
                <button type="button" className="textlink" onClick={() => navigate(ROUTES.PLUGIN)}>
                  {SIMPLE_PREMIERE.more}
                </button>
              </div>
            </div>
            <div className="pr-shot">
              <img src="/premiere-real.png" alt={SIMPLE_PREMIERE.shotAlt} />
            </div>
          </div>
        </div>
      </section>

      <section className="s" id="demos">
        <div className="wrap center">
          <h2>{SIMPLE_DEMOS.title}</h2>
          <p className="lede">{SIMPLE_DEMOS.sub}</p>
          <div className="demos">
            {SIMPLE_DEMOS.items.map((d) => (
              <button type="button" className="demo" key={d.id} onClick={() => setDemo(d.id as DemoId)}>
                <div className="pic" style={{ backgroundImage: `url(${DEMO_MEDIA[d.id as DemoId].poster})` }} />
                <div className="b">
                  <h3>{d.h}</h3>
                  <p>{d.p}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="wrap">
        <div className="price">
          <div>
            <h2>{SIMPLE_PRICING.title}</h2>
            <p>{SIMPLE_PRICING.body}</p>
          </div>
          <button type="button" className="btn" onClick={() => navigate(ROUTES.PRICING)}>
            {SIMPLE_PRICING.cta}
          </button>
        </div>
      </div>

      <footer>
        <div className="wrap">
          <span>{SIMPLE_FOOTER.copyright}</span>
          {SIMPLE_FOOTER.links.map((l) => (
            <button type="button" key={l.to} onClick={() => navigate(l.to)}>
              {l.label}
            </button>
          ))}
          <p className="tm">{SIMPLE_FOOTER.trademarks}</p>
        </div>
      </footer>

      {demo && <DemoModal id={demo} onClose={() => setDemo(null)} />}

      {sheet}
    </div>
  );
};

export default observer(SimplePage);
